import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  PartyPopper,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Gem,
  Gift,
  Cake,
  Baby,
  GraduationCap,
  Calendar,
  Music,
  Camera,
  Palette,
  Crown,
  User
} from 'lucide-react';
import { toast } from 'sonner';

// Party/Event types
const EVENT_TYPES = [
  { value: 'compleanno_bambino', label: 'Compleanno bambino', icon: Baby },
  { value: 'compleanno_adulto', label: 'Compleanno adulto', icon: Cake },
  { value: 'compleanno_importante', label: 'Compleanno importante (18, 30, 40, 50...)', icon: Crown },
  { value: 'battesimo', label: 'Battesimo', icon: Heart },
  { value: 'comunione', label: 'Prima Comunione', icon: Star },
  { value: 'cresima', label: 'Cresima', icon: Star },
  { value: 'laurea', label: 'Festa di Laurea', icon: GraduationCap },
  { value: 'baby_shower', label: 'Baby Shower', icon: Baby },
  { value: 'gender_reveal', label: 'Gender Reveal', icon: Heart },
  { value: 'pensione', label: 'Festa di Pensionamento', icon: PartyPopper },
  { value: 'anniversario', label: 'Anniversario', icon: Heart },
  { value: 'festa_tema', label: 'Festa a tema', icon: Sparkles },
  { value: 'capodanno', label: 'Capodanno / Veglione', icon: PartyPopper },
  { value: 'carnevale', label: 'Carnevale', icon: PartyPopper },
  { value: 'halloween', label: 'Halloween', icon: PartyPopper },
  { value: 'altro', label: 'Altro evento', icon: Calendar },
];

// Product categories for parties
const PRODUCT_CATEGORIES = [
  { value: 'inviti', label: 'Inviti e partecipazioni', icon: MessageSquare },
  { value: 'decorazioni', label: 'Decorazioni e allestimenti', icon: Sparkles },
  { value: 'centrotavola', label: 'Centrotavola', icon: Star },
  { value: 'bomboniere', label: 'Bomboniere e segnaposto', icon: Gift },
  { value: 'torta', label: 'Torta personalizzata', icon: Cake },
  { value: 'sweet_table', label: 'Sweet table / Confettata', icon: Cake },
  { value: 'palloncini', label: 'Composizioni palloncini', icon: PartyPopper },
  { value: 'photo_booth', label: 'Photo booth props', icon: Camera },
  { value: 'banner', label: 'Banner e striscioni', icon: PartyPopper },
  { value: 'tableau', label: 'Tableau e cartellonistica', icon: Palette },
  { value: 'gadget', label: 'Gadget personalizzati', icon: Gift },
  { value: 'costumi', label: 'Costumi e accessori', icon: Crown },
  { value: 'altro', label: 'Altro', icon: Sparkles },
];

// Themes
const THEMES = [
  { value: 'elegante', label: 'Elegante / Chic', icon: Gem },
  { value: 'rustico', label: 'Rustico / Country', icon: Heart },
  { value: 'boho', label: 'Boho / Bohemien', icon: Sparkles },
  { value: 'vintage', label: 'Vintage / Retrò', icon: Star },
  { value: 'moderno', label: 'Moderno / Minimal', icon: Zap },
  { value: 'fiabesco', label: 'Fiabesco / Principesse', icon: Crown },
  { value: 'supereroi', label: 'Supereroi', icon: Zap },
  { value: 'cartoon', label: 'Cartoon / Personaggi', icon: Star },
  { value: 'tropicale', label: 'Tropicale / Hawaiian', icon: Sparkles },
  { value: 'marino', label: 'Marino / Nautico', icon: Star },
  { value: 'safari', label: 'Safari / Giungla', icon: Heart },
  { value: 'spazio', label: 'Spazio / Astronauti', icon: Star },
  { value: 'unicorni', label: 'Unicorni / Arcobaleno', icon: Sparkles },
  { value: 'calcio', label: 'Calcio / Sport', icon: Star },
  { value: 'musica', label: 'Musica / Disco', icon: Music },
  { value: 'personalizzato', label: 'Tema personalizzato', icon: Palette },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Heart },
];

// Color schemes
const COLOR_SCHEMES = [
  { value: 'rosa_oro', label: 'Rosa e oro', icon: Sparkles },
  { value: 'blu_argento', label: 'Blu e argento', icon: Star },
  { value: 'bianco_oro', label: 'Bianco e oro', icon: Crown },
  { value: 'verde_oro', label: 'Verde e oro', icon: Heart },
  { value: 'pastello', label: 'Colori pastello', icon: Sparkles },
  { value: 'arcobaleno', label: 'Arcobaleno', icon: Sparkles },
  { value: 'nero_oro', label: 'Nero e oro', icon: Gem },
  { value: 'rosso_bianco', label: 'Rosso e bianco', icon: Heart },
  { value: 'rosa_fucsia', label: 'Rosa e fucsia', icon: Heart },
  { value: 'blu_bianco', label: 'Blu e bianco', icon: Star },
  { value: 'naturale', label: 'Toni naturali / Neutri', icon: Heart },
  { value: 'personalizzato', label: 'Colori personalizzati', icon: Palette },
];

// Number of guests
const GUEST_COUNTS = [
  { value: '10-20', label: '10-20 invitati', description: 'Festa intima' },
  { value: '20-40', label: '20-40 invitati', description: 'Festa media' },
  { value: '40-70', label: '40-70 invitati', description: 'Festa grande' },
  { value: '70-100', label: '70-100 invitati', description: 'Grande evento' },
  { value: '100+', label: '100+ invitati', description: 'Evento molto grande' },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '50-150', label: '€50 - €150', description: 'Decorazioni base' },
  { value: '150-300', label: '€150 - €300', description: 'Allestimento completo' },
  { value: '300-500', label: '€300 - €500', description: 'Festa curata' },
  { value: '500-800', label: '€500 - €800', description: 'Evento elaborato' },
  { value: '800-1500', label: '€800 - €1500', description: 'Festa premium' },
  { value: '1500+', label: '€1500+', description: 'Evento esclusivo' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '3-4weeks', label: '3-4 Settimane', description: 'Standard' },
  { value: '1-2months', label: '1-2 Mesi', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example parties
const PARTY_EXAMPLES = [
  {
    idea: "Festa dei 40 anni elegante con tema oro e nero",
    result: "Allestimento completo: inviti letterpress, centrotavola con candele, backdrop fotografico glitterato, sweet table coordinata",
    image: "https://images.unsplash.com/photo-1745433816908-e7fd77b1e45d?w=400&h=300&fit=crop",
    category: "Compleanno"
  },
  {
    idea: "Battesimo per la mia bimba, tema angeli e nuvole",
    result: "Bomboniere in ceramica dipinte a mano, centrotavola con fiori bianchi, tableau con illustrazione personalizzata",
    image: "https://images.unsplash.com/photo-1776267890230-94f1926c30ef?w=400&h=300&fit=crop",
    category: "Battesimo"
  },
  {
    idea: "Festa di laurea con tema viaggio",
    result: "Inviti a forma di biglietto aereo, tableau con mappamondo, bomboniere con bussola incisa, photo booth props tema viaggio",
    image: "https://images.unsplash.com/photo-1616315168316-61d6e5f16ead?w=400&h=300&fit=crop",
    category: "Laurea"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Per i 50 anni di mia mamma volevo qualcosa di speciale. L'artigiana ha creato un allestimento da sogno con fiori di carta fatti a mano e un tableau personalizzato. Gli invitati sono rimasti a bocca aperta!",
    author: "Francesca L.",
    location: "Milano"
  },
  {
    text: "Il battesimo di nostro figlio è stato perfetto grazie alle bomboniere artigianali e ai centrotavola coordinati. Tutto curato nei minimi dettagli, qualità che non si trova nei negozi.",
    author: "Marco e Giulia",
    location: "Roma"
  },
  {
    text: "Festa di laurea indimenticabile! Dalla confettata alle decorazioni, tutto era personalizzato con il mio nome e il tema che avevo scelto. Un ricordo che conserverò per sempre.",
    author: "Alessia V.",
    location: "Firenze"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Con quanto anticipo devo ordinare per una festa?",
    answer: "Dipende dalla complessità: per decorazioni semplici bastano 2 settimane, per allestimenti completi con bomboniere personalizzate consigliamo almeno 4-6 settimane. Per eventi importanti (matrimoni, battesimi), meglio 2-3 mesi di anticipo."
  },
  {
    question: "Posso avere un allestimento coordinato completo?",
    answer: "Assolutamente sì! Molti artigiani offrono pacchetti completi che includono inviti, decorazioni, centrotavola, bomboniere, tableau e sweet table, tutto perfettamente coordinato nel tema e nei colori scelti."
  },
  {
    question: "Come funziona per le bomboniere?",
    answer: "Puoi scegliere tra bomboniere già pronte o completamente personalizzate. Per battesimi, comunioni e cresime sono molto richieste ceramiche dipinte a mano, sacchettini ricamati, o oggetti in legno inciso."
  },
  {
    question: "Fate anche torte personalizzate?",
    answer: "Sì! Collaboriamo con cake designer artigianali che creano torte scenografiche, cupcake decorati e sweet table complete. Dalla torta con pasta di zucchero scolpita alle naked cake con fiori freschi."
  },
  {
    question: "Posso avere un tema completamente inventato?",
    answer: "Certamente! Gli artigiani amano le sfide creative. Descrivi la tua idea, anche la più originale, e riceverai proposte su come realizzarla. Hai un tema specifico in mente? Faccelo sapere!"
  }
];

export default function FesteRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: '',
    product_category: '',
    theme: '',
    color_scheme: '',
    guest_count: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Feste e Eventi Su Misura | Decorazioni Artigianali | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `party-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi la festa o evento che vuoi organizzare');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_party_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const eventTypeLabel = EVENT_TYPES.find(t => t.value === formData.event_type)?.label || '';
      const productLabel = PRODUCT_CATEGORIES.find(t => t.value === formData.product_category)?.label || '';
      const themeLabel = THEMES.find(t => t.value === formData.theme)?.label || '';
      const colorLabel = COLOR_SCHEMES.find(t => t.value === formData.color_scheme)?.label || '';
      const guestLabel = GUEST_COUNTS.find(t => t.value === formData.guest_count)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (eventTypeLabel) details.push(`🎉 Evento: ${eventTypeLabel}`);
      if (productLabel) details.push(`🎁 Cerco: ${productLabel}`);
      if (themeLabel) details.push(`✨ Tema: ${themeLabel}`);
      if (colorLabel) details.push(`🎨 Colori: ${colorLabel}`);
      if (guestLabel) details.push(`👥 Invitati: ${guestLabel}`);
      if (budgetLabel) details.push(`💰 Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️ Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n🎊 DETTAGLI EVENTO\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${eventTypeLabel} - ${themeLabel}`.trim() || 'Festa su misura',
        description: fullDescription,
        category: 'Feste',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dagli artigiani.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('party-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Feste e Eventi Su Misura | Decorazioni Artigianali | Rarifex</title>
        <meta name="description" content="Organizza la festa perfetta con decorazioni artigianali italiane. Compleanni, battesimi, lauree: inviti, bomboniere, allestimenti personalizzati. Gratuito e senza impegno." />
        <meta name="keywords" content="festa su misura, decorazioni festa, bomboniere artigianali, inviti personalizzati, allestimento festa, compleanno, battesimo, laurea, sweet table" />
        <link rel="canonical" href="https://www.rarifex.com/feste-su-misura" />
        <meta property="og:title" content="Feste e Eventi Su Misura | Decorazioni Artigianali | Rarifex" />
        <meta property="og:description" content="Organizza la festa perfetta con decorazioni artigianali italiane. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/feste-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-pink-50/50 via-white to-purple-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-pink-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <PartyPopper className="w-4 h-4" />
                    Feste Artigianali
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    La festa perfetta{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">
                      creata per te
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Artigiani italiani creano decorazioni uniche per il tuo evento speciale.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Inviti, bomboniere, allestimenti, sweet table: tutto personalizzato.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-pink-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-slate-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="party-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Nome del tuo evento
                        </Label>
                        <Input
                          placeholder="Es: Festa dei 30 anni di Giulia"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="party-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi la festa che sogni *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Che tipo di festa è? Qual è il tema? Cosa vorresti di speciale? Allega foto di ispirazione se ne hai."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="party-description-textarea"
                        />
                      </div>
                      
                      {/* Event Type & Product */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Tipo di evento
                          </Label>
                          <select
                            value={formData.event_type}
                            onChange={(e) => handleInputChange('event_type', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            data-testid="party-event-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {EVENT_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Cosa cerchi?
                          </Label>
                          <select
                            value={formData.product_category}
                            onChange={(e) => handleInputChange('product_category', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            data-testid="party-product-select"
                          >
                            <option value="">Seleziona...</option>
                            {PRODUCT_CATEGORIES.map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Theme & Colors */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tema</Label>
                          <select
                            value={formData.theme}
                            onChange={(e) => handleInputChange('theme', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            data-testid="party-theme-select"
                          >
                            <option value="">Seleziona...</option>
                            {THEMES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Colori</Label>
                          <select
                            value={formData.color_scheme}
                            onChange={(e) => handleInputChange('color_scheme', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            data-testid="party-color-select"
                          >
                            <option value="">Seleziona...</option>
                            {COLOR_SCHEMES.map(c => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Guests & Budget */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Numero invitati</Label>
                          <select
                            value={formData.guest_count}
                            onChange={(e) => handleInputChange('guest_count', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            data-testid="party-guests-select"
                          >
                            <option value="">Seleziona...</option>
                            {GUEST_COUNTS.map(g => (
                              <option key={g.value} value={g.value}>{g.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            data-testid="party-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Quando è la festa?</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                          data-testid="party-timeline-select"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label} - {opt.description}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Attachments */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Foto di ispirazione (Pinterest, Instagram...)
                        </Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-pink-300 hover:bg-pink-50/50 transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploadingFile}
                                data-testid="party-file-upload"
                              />
                              {uploadingFile ? (
                                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              ) : (
                                <Upload className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="text-sm text-slate-500">
                                Carica ispirazioni ({formData.attachments.length}/5)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-lg shadow-pink-200"
                        data-testid="party-submit-button"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <PartyPopper className="w-5 h-5 mr-2" />
                        )}
                        Ricevi proposte gratis
                      </Button>
                      
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROBLEMA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                Le decorazioni dei negozi sono tutte uguali e senz'anima
              </h2>
              
              <div className="space-y-4 text-lg text-slate-600 mb-8">
                <p className="flex items-center justify-center gap-3">
                  <span className="text-slate-400">✗</span>
                  Kit di decorazioni visti e rivisti
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-slate-400">✗</span>
                  Bomboniere tutte identiche senza personalizzazione
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-slate-400">✗</span>
                  Impossibile trovare esattamente quello che immagini
                </p>
              </div>
              
              <p className="text-xl text-slate-700 font-medium">
                La tua festa merita decorazioni uniche come il tuo evento.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: SOLUZIONE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-pink-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Una festa{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">
                  unica come te
                </span>
              </h2>
              
              <p className="text-xl text-slate-600 mb-12">
                Artigiani italiani creano tutto quello che serve per la tua festa perfetta.
                <br />
                <strong className="text-slate-800">Tu sogni, loro realizzano.</strong>
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-pink-100 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-pink-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Descrivi la tua festa</h3>
                  <p className="text-slate-600">Tema, colori, stile, numero di invitati</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Ricevi proposte</h3>
                  <p className="text-slate-600">Artigiani ti contattano con idee e preventivi</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Scegli e festeggia</h3>
                  <p className="text-slate-600">Nessun obbligo, decidi solo se ti convince</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: ESEMPI */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
                Da idea a festa indimenticabile
              </h2>
              <p className="text-xl text-slate-600 text-center mb-12">
                Ecco cosa hanno creato i nostri artigiani
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {PARTY_EXAMPLES.map((example, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <img 
                      src={example.image} 
                      alt={example.result}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <span className="text-xs font-medium text-pink-600 uppercase tracking-wider">
                        {example.category}
                      </span>
                      <p className="text-sm text-slate-500 mt-2 mb-1">
                        Richiesta: "{example.idea}"
                      </p>
                      <p className="font-semibold text-slate-800">
                        → {example.result}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button 
                  onClick={scrollToForm}
                  variant="outline"
                  className="border-pink-200 text-pink-700 hover:bg-pink-50"
                >
                  Vuoi qualcosa di simile? Descrivi la tua idea
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5: COSA PUOI RICHIEDERE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50/50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Tutto per la tua festa, fatto a mano
              </h2>
              
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { icon: MessageSquare, label: 'Inviti personalizzati', desc: 'Letterpress, acquerello, calligrafia' },
                  { icon: Gift, label: 'Bomboniere uniche', desc: 'Ceramica, tessuto, legno inciso' },
                  { icon: Sparkles, label: 'Decorazioni', desc: 'Fiori di carta, ghirlande, backdrop' },
                  { icon: Star, label: 'Centrotavola', desc: 'Composizioni floreali, candele, creativi' },
                  { icon: Cake, label: 'Torte e dolci', desc: 'Cake design, sweet table, confettata' },
                  { icon: Camera, label: 'Photo booth', desc: 'Props, cornici, accessori divertenti' },
                  { icon: Palette, label: 'Tableau e grafica', desc: 'Cartellonistica coordinata' },
                  { icon: PartyPopper, label: 'Palloncini', desc: 'Composizioni artistiche' },
                  { icon: Crown, label: 'Costumi e accessori', desc: 'Per feste a tema' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-4 rounded-xl bg-white shadow-sm">
                    <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">{item.label}</h3>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Cosa dicono i clienti
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {TESTIMONIALS.map((testimonial, idx) => (
                  <Card key={idx} className="p-6 bg-white">
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className="w-5 h-5 fill-pink-500 text-pink-500" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center font-semibold text-pink-700">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-medium">{testimonial.author}</p>
                        <p className="text-slate-500 text-xs">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: FAQ */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-pink-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Domande frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-pink-50 transition-colors"
                      data-testid={`party-faq-${idx}`}
                    >
                      <span className="font-medium text-slate-800">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: CTA FINALE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-pink-100 to-pink-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <PartyPopper className="w-16 h-16 text-pink-600 mx-auto mb-6" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Pronto a organizzare la festa perfetta?
              </h2>
              
              <p className="text-xl text-slate-600 mb-8">
                Gli artigiani italiani aspettano di creare qualcosa di unico per te.
              </p>
              
              <Button
                onClick={scrollToForm}
                size="lg"
                className="py-6 px-10 text-lg font-semibold bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-lg shadow-pink-200"
                data-testid="party-cta-final-button"
              >
                <PartyPopper className="w-5 h-5 mr-2" />
                Ricevi proposte gratis ora
              </Button>
            </div>
          </div>
        </section>

      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">
              Per inviare la tua richiesta e ricevere proposte dagli artigiani, devi prima accedere o creare un account.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAuthPrompt(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={() => navigate('/login?redirect=/feste-su-misura')}
                className="flex-1 bg-pink-500 hover:bg-pink-600"
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
